import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../redux";
import { fetchContracts, updateContract } from "../../redux/Slices/contractSlice";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import { FileText, MessageCircle, Star, User, Edit, DollarSign, Loader, CheckCircle, X } from "lucide-react";
import { ContractStatusEnum, RefundRequestStatusEnum } from "../../modules";
import ContractMilestoneModal from "../../components/ui/ContractMilestoneModal";
import MilestoneDialog from "../../components/ui/MilestoneDialog";
import { useNavigate } from "react-router-dom";
import ContractCompletionModal from "../../components/ui/ContractCompletionModal";
import ContractClosingModal from "../../components/ui/ContractClosingModal";
import { getDynamicCurrencySymbol } from "../../lib/currencyUtils";
import { useTranslation } from "react-i18next";

const ActiveContractsTab: React.FC<{ onCountChange?: (count: number) => void; statusFilter?: ContractStatusEnum[]; onContractCompleted?: () => void }> = ({ onCountChange, statusFilter, onContractCompleted }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { user } = useSelector((state: any) => state.auth);

    const [items, setItems] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ currentPage: 1, limit: 10, totalCount: 0, totalPages: 0, hasNextPage: false });
    const [showContractModal, setShowContractModal] = useState(false);
    const [selectedContract, setSelectedContract] = useState<any>(null);
    const [showMilestoneDialog, setShowMilestoneDialog] = useState(false);
    const [selectedContractForMilestones, setSelectedContractForMilestones] = useState<any>(null);
    const [loadingMilestoneDialog, setLoadingMilestoneDialog] = useState(false);
    const [showCompletionModal, setShowCompletionModal] = useState(false);
    const [selectedContractForCompletion, setSelectedContractForCompletion] = useState<any>(null);
    const [completingContract, setCompletingContract] = useState(false);
    const [showClosingModal, setShowClosingModal] = useState(false);
    const [selectedContractForClosing, setSelectedContractForClosing] = useState<any>(null);
    const [closingContract, setClosingContract] = useState(false);

    const load = async (page = 1, limit = 10) => {
        if (!user?.id) return;
        setLoading(true);
        try {
            const fetchParams: any = {
                page,
                limit,
                clientId: user.id,
                sort: JSON.stringify([{ orderBy: "createdAt", order: "DESC" }])
            };

            // Add status filter if provided
            if (statusFilter) {
                fetchParams.filters = JSON.stringify({ statusIds: statusFilter });
            }

            const res: any = await dispatch(fetchContracts(fetchParams));
            if (res?.meta?.requestStatus === "fulfilled") {
                const payload = res.payload || {};
                let data = payload.data || [];
                if (!statusFilter) {
                    data = data.filter(c => Number(c?.status?.id) !== ContractStatusEnum.Closed && Number(c?.status?.id) !== ContractStatusEnum.Completed);
                }
                setItems(data);
                const totalCount = payload.totalCount || 0;
                const totalPages = Math.ceil(totalCount / limit) || 0;
                setPagination({ currentPage: page, limit, totalCount, totalPages, hasNextPage: page < totalPages });

                // Update count only if onCountChange is provided and we're loading the first page
                // or if it's a refresh after contract completion
                if (onCountChange && (page === 1 || statusFilter)) {
                    onCountChange(totalCount);
                }
            }
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        load(1, pagination.limit); 
    }, [user?.id, statusFilter]);



    const handlePageChange = (p: number) => {
        if (p >= 1 && p <= pagination.totalPages) load(p, pagination.limit);
    };

    // Helper function to get contract status text
    const getContractStatusText = (statusId: number) => {
        switch (Number(statusId)) {
            case ContractStatusEnum.Pending: return t("activeContractsTab.pending");
            case ContractStatusEnum.Active: return t("activeContractsTab.active");
            case ContractStatusEnum.Cancelled: return t("activeContractsTab.cancelled");
            case ContractStatusEnum.Completed: return t("activeContractsTab.completed");
            case ContractStatusEnum.Archived: return t("activeContractsTab.archived");
            case ContractStatusEnum.Suspended: return t("activeContractsTab.suspended");
            case ContractStatusEnum.Deleted: return t("activeContractsTab.deleted");
            case ContractStatusEnum.Paused: return t("activeContractsTab.paused");
            case ContractStatusEnum.Expired: return t("activeContractsTab.expired");
            case ContractStatusEnum.Rejected: return t("activeContractsTab.rejected");
            case ContractStatusEnum.Accepted: return t("activeContractsTab.accepted");
            case ContractStatusEnum.Closed: return t("activeContractsTab.closed");
            default: return t("activeContractsTab.unknown");
        }
    };

    // Helper function to get contract status color
    const getContractStatusColor = (statusId: number) => {
        switch (Number(statusId)) {
            case ContractStatusEnum.Pending: return "bg-yellow-100 text-yellow-800";
            case ContractStatusEnum.Active: return "bg-blue-100 text-blue-800";
            case ContractStatusEnum.Cancelled: return "bg-red-100 text-red-800";
            case ContractStatusEnum.Completed: return "bg-green-100 text-green-800";
            case ContractStatusEnum.Archived: return "bg-gray-100 text-gray-800";
            case ContractStatusEnum.Suspended: return "bg-orange-100 text-orange-800";
            case ContractStatusEnum.Deleted: return "bg-red-100 text-red-800";
            case ContractStatusEnum.Paused: return "bg-yellow-100 text-yellow-800";
            case ContractStatusEnum.Expired: return "bg-red-100 text-red-800";
            case ContractStatusEnum.Rejected: return "bg-red-100 text-red-800";
            case ContractStatusEnum.Accepted: return "bg-green-100 text-green-800";
            case ContractStatusEnum.Closed: return "bg-gray-100 text-gray-800";
            default: return "bg-gray-100 text-gray-800";
        }
    };

    // Helper function to calculate platform fees (fallback)
    const calculatePlatformFee = (amount: number) => amount * 0.02; // 2% platform fee
    const calculateTotalWithFee = (amount: number, emergencyCost: number = 0, trackerAmount: number = 0) => {
        const baseAmount = amount + emergencyCost + trackerAmount;
        return baseAmount + calculatePlatformFee(baseAmount);
    };

    // Helper to aggregate platform fee/total from escrow records
    const getEscrowFinancialSummary = (contract: any) => {
        const escrows = Array.isArray(contract?.escrows) ? contract.escrows : [];
        if (escrows.length === 0) {
            return null;
        }

        return escrows.reduce(
            (acc: { platformFee: number; totalAmount: number }, escrow: any) => {
                const fee = Number(escrow?.platformFee ?? 0);
                const total = Number(escrow?.totalAmount ?? 0);

                return {
                    platformFee: acc.platformFee + (Number.isFinite(fee) ? fee : 0),
                    totalAmount: acc.totalAmount + (Number.isFinite(total) ? total : 0),
                };
            },
            { platformFee: 0, totalAmount: 0 }
        );
    };

    // Helper function to get payment status from escrow data within contract
    const getPaymentStatus = (contract: any) => {
        const escrows = contract.escrows || [];
        if (escrows.length === 0) return "no_payment";

        // Check if any escrow has refunded status
        const hasRefunded = escrows.some((escrow: any) => {
            const statusName = escrow.status?.name?.toLowerCase() || escrow.status?.toString().toLowerCase() || "";
            return statusName === "refunded";
        });

        if (hasRefunded) return "refunded";

        // Check for other statuses
        const firstEscrow = escrows[0];
        const statusName = firstEscrow?.status?.name?.toLowerCase() || firstEscrow?.status?.toString().toLowerCase() || "";

        switch (statusName) {
            case "paid":
            case "funded":
            case "completed":
                return "paid";
            case "pending":
            case "created":
                return "pending";
            case "failed":
            case "cancelled":
                return "failed";
            case "released":
                return "released";
            default:
                return "pending";
        }
    };

    // Helper function to get payment status text
    const getPaymentStatusText = (paymentStatus: string) => {
        switch (paymentStatus) {
            case "paid":
            case "completed":
                return t("activeContractsTab.paid");
            case "pending":
            case "created":
                return t("activeContractsTab.pendingPayment");
            case "failed":
            case "cancelled":
                return t("activeContractsTab.paymentFailed");
            case "released":
                return t("activeContractsTab.released");
            case "refunded":
                return t("activeContractsTab.refunded");
            default:
                return t("activeContractsTab.noPayment");
        }
    };

    // Helper function to get payment status color
    const getPaymentStatusColor = (paymentStatus: string) => {
        switch (paymentStatus) {
            case "paid":
            case "completed":
                return "text-green-600 bg-green-100";
            case "pending":
            case "created":
                return "text-yellow-600 bg-yellow-100";
            case "failed":
            case "cancelled":
                return "text-red-600 bg-red-100";
            case "released":
                return "text-blue-600 bg-blue-100";
            case "refunded":
                return "text-green-600 bg-green-100";
            default:
                return "text-gray-600 bg-gray-100";
        }
    };

    // Helper function to get refund request status info
    const getRefundRequestInfo = (contract: any) => {
        const refundRequest = contract.refundRequest;
        if (!refundRequest) return null;

        const statusId = Number(refundRequest.status?.id);

        switch (statusId) {
            case RefundRequestStatusEnum.Active:
                return {
                    text: t("activeContractsTab.refundRequested"),
                    className: "bg-yellow-100 text-yellow-800",
                    showReason: false
                };
            case RefundRequestStatusEnum.Approved:
                return {
                    text: t("activeContractsTab.refundRequestApproved"),
                    className: "bg-green-100 text-green-800",
                    showReason: false
                };
            case RefundRequestStatusEnum.Rejected:
                return {
                    text: t("activeContractsTab.refundRequestRejected"),
                    className: "bg-red-100 text-red-800",
                    showReason: true,
                    reason: refundRequest.rejectReason || t("activeContractsTab.noReasonProvided")
                };
            default:
                return {
                    text: t("activeContractsTab.refundRequest"),
                    className: "bg-gray-100 text-gray-800",
                    showReason: false
                };
        }
    };

    // Contract type badge component
    const ContractTypeBadge = ({ contractType, isHourly, weeklyLimit }) => {
        if (isHourly) {
            return (
                <div className="flex items-center gap-2">
                    <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded-full">
                        {t("activeContractsTab.hourly")}
                    </span>
                    <span className="text-xs text-gray-500">
                        {weeklyLimit}{t("activeContractsTab.hoursPerWeek")}
                    </span>
                </div>
            );
        }
        return (
            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                {t("activeContractsTab.fixedPrice")}
            </span>
        );
    };

    const handleViewAppointment = (contract: any) => {
        setSelectedContract(contract);
        setShowContractModal(true);
    };

    const handleCloseContractModal = () => {
        setShowContractModal(false);
        setSelectedContract(null);
        // Reload contracts to reflect any changes
        load(pagination.currentPage, pagination.limit);
    };

    const handleViewDeliverables = async (contract: any) => {
        setLoadingMilestoneDialog(true);
        try {
            // Validate contract has an ID
            if (!contract || !contract.id) {
                console.error("Invalid contract, missing ID");
                return;
            }

            // Set the contract and show the dialog
            setSelectedContractForMilestones(contract);
            setShowMilestoneDialog(true);
        } catch (error) {
            console.error("Failed to open milestone dialog:", error);
        } finally {
            // Reset loading state after a short delay to allow dialog to open
            setTimeout(() => {
                setLoadingMilestoneDialog(false);
            }, 500);
        }
    };

    const handleCloseMilestoneDialog = () => {
        setShowMilestoneDialog(false);
        setSelectedContractForMilestones(null);
        // Reload contracts to reflect any changes
        load(pagination.currentPage, pagination.limit);
    };

    const handleCompleteContract = (contract: any) => {
        setSelectedContractForCompletion(contract);
        setShowCompletionModal(true);
    };

    const handleCloseCompletionModal = () => {
        setShowCompletionModal(false);
        setSelectedContractForCompletion(null);
    };

    const handleCloseContract = (contract: any) => {
        setSelectedContractForClosing(contract);
        setShowClosingModal(true);
    };

    const handleCloseClosingModal = () => {
        setShowClosingModal(false);
        setSelectedContractForClosing(null);
    };

    const handleContractCompletion = async (reason: string) => {
        if (!selectedContractForCompletion) return;

        setCompletingContract(true);
        try {
            // Update the count immediately for better UX
            if (onCountChange && pagination.totalCount > 0) {
                onCountChange(pagination.totalCount - 1);
            }

            // Update local state immediately to reflect the change
            setItems(prevItems => prevItems.filter(item => item.id !== selectedContractForCompletion.id));
            setPagination(prevPagination => ({
                ...prevPagination,
                totalCount: Math.max(0, prevPagination.totalCount - 1)
            }));

            await dispatch(updateContract({
                id: selectedContractForCompletion.id,
                data: {
                    status: { id: ContractStatusEnum.Completed },
                    reason: reason
                }
            })).unwrap();

            handleCloseCompletionModal();

            // Call the onContractCompleted callback to switch tabs
            if (onContractCompleted) {
                onContractCompleted();
            }
        } catch (error) {
            console.error("Failed to complete contract:", error);

            // Revert the count change if the API call failed
            if (onCountChange) {
                load(pagination.currentPage, pagination.limit);
            }
        } finally {
            setCompletingContract(false);
        }
    };

    const handleContractClosing = async (closeReason: string) => {
        if (!selectedContractForClosing) return;

        setClosingContract(true);
        try {
            // Update the count immediately for better UX
            if (onCountChange && pagination.totalCount > 0) {
                onCountChange(pagination.totalCount - 1);
            }

            // Update local state immediately to reflect the change
            setItems(prevItems => prevItems.filter(item => item.id !== selectedContractForClosing.id));
            setPagination(prevPagination => ({
                ...prevPagination,
                totalCount: Math.max(0, prevPagination.totalCount - 1)
            }));

            await dispatch(updateContract({
                id: selectedContractForClosing.id,
                data: {
                    status: { id: ContractStatusEnum.Closed },
                    closeReason: closeReason
                }
            })).unwrap();

            handleCloseClosingModal();

            // Call the onContractCompleted callback to switch tabs
            if (onContractCompleted) {
                onContractCompleted();
            }
        } catch (error) {
            console.error("Failed to close contract:", error);

            // Revert the count change if the API call failed
            if (onCountChange) {
                load(pagination.currentPage, pagination.limit);
            }
        } finally {
            setClosingContract(false);
        }
    };

    return (
        <>
            {loading ? (
                <div className="flex items-center justify-center py-12">
                    <div className="flex flex-col items-center gap-4">
                        <Loader className="h-8 w-8 animate-spin text-blue-600" />
                        <p className="text-gray-600">{t("activeContractsTab.loadingContracts")}</p>
                    </div>
                </div>
            ) : items.length > 0 ? (
                <div className="space-y-4 sm:space-y-6">
                    {items.map((c) => {
                        const contractType = Number(c?.type?.id) === 2 ? "hourly" : "fixed";
                        const isHourly = contractType === "hourly";

                        // Check if contract is emergency and get emergency cost from first milestone
                        const isEmergency = c?.offer?.job?.isEmergency || c?.isEmergency || false;
                        const firstMilestone = c?.milestones?.[0];
                        const emergencyCost = isEmergency && firstMilestone ? Number(firstMilestone.emergencyCost || 0) : 0;
                        const trackerCost = firstMilestone ? Number(firstMilestone.trackerAmount || 0) : 0;

                        const weeklyAmount = isHourly ? (Number(c.amount || 0) * Number(c.weeklyLimit || 0)) : Number(c.amount || 0);
                        const escrowFinancials = getEscrowFinancialSummary(c);
                        const platformFee = escrowFinancials?.platformFee ?? calculatePlatformFee(weeklyAmount + emergencyCost + trackerCost);
                        const totalAmount = escrowFinancials?.totalAmount ?? calculateTotalWithFee(weeklyAmount, emergencyCost, trackerCost);
                        const paymentStatus = getPaymentStatus(c);
                        const refundRequestInfo = getRefundRequestInfo(c);

                        return (
                            <Card key={c.id} className="p-3 sm:p-4 md:p-6 hover:shadow-lg transition-shadow" style={{ textAlign: "left" }}>
                                <div className="flex flex-col gap-3 sm:gap-4">
                                    <div className="flex flex-col gap-2">
                                        <div className="space-y-2">
                                            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                                                <h3 className="text-base sm:text-lg font-semibold text-gray-900 break-words">{c.title || t("activeContractsTab.contract")}</h3>
                                                <ContractTypeBadge
                                                    contractType={contractType}
                                                    isHourly={isHourly}
                                                    weeklyLimit={c.weeklyLimit || 0}
                                                />
                                                <Badge variant={undefined} className={`text-xs ${getContractStatusColor(Number(c?.status?.id))}`}>
                                                    {getContractStatusText(Number(c?.status?.id))}
                                                </Badge>
                                                {isEmergency && emergencyCost > 0 && (
                                                    <span className="px-2 py-0.5 sm:py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full whitespace-nowrap">
                                                        {t("activeContractsTab.emergency")} +{getDynamicCurrencySymbol()}{emergencyCost.toFixed(2)}
                                                    </span>
                                                )}
                                                {trackerCost > 0 && (
                                                    <span className="px-2 py-0.5 sm:py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full whitespace-nowrap">
                                                        {t("activeContractsTab.tracker")} +{getDynamicCurrencySymbol()}{trackerCost.toFixed(2)}
                                                    </span>
                                                )}
                                                {refundRequestInfo && (
                                                    <span className={`px-2 py-0.5 sm:py-1 ${refundRequestInfo.className} text-xs font-medium rounded-full whitespace-nowrap`}>
                                                        {refundRequestInfo.text}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-xs sm:text-sm text-gray-600">{t("activeContractsTab.contractId")} {c.id}</div>
                                            {refundRequestInfo?.showReason && (
                                                <div className="text-xs text-red-600 break-words">
                                                    {t("activeContractsTab.reason")} {refundRequestInfo.reason}
                                                </div>
                                            )}
                                        </div>
                                    </div>


                                    <p className="text-sm sm:text-base text-gray-600 line-clamp-3 break-words">{c.description || t("activeContractsTab.noDescription")}</p>

                                    {/* Payment Status */}
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getPaymentStatusColor(paymentStatus)}`}>
                                            {getPaymentStatusText(paymentStatus)}
                                        </span>
                                        {paymentStatus === "refunded" && (
                                            <span className="px-2 sm:px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                {t("activeContractsTab.fullRefundMessage")}
                                            </span>
                                        )}
                                    </div>

                                    {/* Professional Info */}
                                    <div className="flex items-center gap-3 sm:gap-4">
                                        <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                                            {c?.professional?.photo?.path ? (
                                                <img
                                                    src={c?.professional?.photo?.path}
                                                    alt={c?.professional?.firstName + " " + c?.professional?.lastName || "Professional"}
                                                    className="h-full w-full object-cover"
                                                />
                                            ) : (
                                                <User className="h-5 w-5 sm:h-6 sm:w-6 text-gray-600" />
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm sm:text-base font-medium truncate">{c.professional.firstName + " " + c.professional.lastName}</div>
                                            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-gray-600">
                                                <Star className="h-3 w-3 sm:h-4 sm:w-4 text-yellow-400 fill-current flex-shrink-0" />
                                                <span>{c.professional?.averageRating || 0}</span>
                                                <span className="whitespace-nowrap">({c.professional?.reviewsCount || "N/A"} {t("activeContractsTab.reviews")})</span>
                                                {c.professional.verified && (
                                                    <Badge variant={undefined} className="bg-green-50 text-green-700 text-xs">{t("activeContractsTab.verified")}</Badge>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    {/* Action Buttons - Improved responsive layout */}
                                    <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
                                        <div className="grid grid-cols-2 gap-2 sm:contents">
                                            <Button 
                                                className="flex items-center justify-center gap-1.5 sm:gap-2 text-xs sm:text-sm h-9 sm:h-10" 
                                                onClick={() => {
                                                    window.location.href = "/client/MessageAndVideoCall?professionalId=" + c.professional.id + "&professionalName=" + encodeURIComponent(c.professional.firstName + " " + c.professional.lastName);
                                                    // navigate(`/client/MessageAndVideoCall?professionalId=${c.professional.id}&professionalName=${c.professional.firstName + " " + c.professional.lastName}`);
                                                }}
                                            >
                                                <MessageCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                                <span className="hidden sm:inline">
                                                    {c.chatUnreadCount?.professional !== undefined ? `${t("activeContractsTab.messages")} (${c.chatUnreadCount.professional})` : t("activeContractsTab.messages")}
                                                </span>
                                                <span className="sm:hidden truncate">
                                                    {c.chatUnreadCount?.professional !== undefined ? c.chatUnreadCount.professional : t("activeContractsTab.msg")}
                                                </span>
                                            </Button>

                                            <Button
                                                variant="outline"
                                                className="flex items-center justify-center gap-1.5 sm:gap-2 text-xs sm:text-sm h-9 sm:h-10"
                                                onClick={() => handleViewDeliverables(c)}
                                                disabled={loadingMilestoneDialog}
                                            >
                                                {loadingMilestoneDialog ? (
                                                    <>
                                                        <Loader className="h-3.5 w-3.5 sm:h-4 sm:w-4 animate-spin" />
                                                        <span className="hidden sm:inline">{t("activeContractsTab.loading")}</span>
                                                        <span className="sm:hidden">...</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <FileText className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                                        <span className="hidden sm:inline">{t("activeContractsTab.viewDeliverables")}</span>
                                                        <span className="sm:hidden">{t("activeContractsTab.deliverables")}</span>
                                                    </>
                                                )}
                                            </Button>
                                        </div>

                                        <div className="grid grid-cols-2 gap-2 sm:contents">
                                            {/* Edit Order Button */}
                                            <Button
                                                variant="outline"
                                                className="flex items-center justify-center gap-1.5 sm:gap-2 text-xs sm:text-sm h-9 sm:h-10"
                                                onClick={() => handleViewAppointment(c)}
                                            >
                                                <Edit className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                                <span className="hidden sm:inline">{t("activeContractsTab.editOrder")}</span>
                                                <span className="sm:hidden">{t("activeContractsTab.edit")}</span>
                                            </Button>

                                            {/* Complete Button - Show for accepted contracts in active tab, or completed contracts in past orders tab */}
                                            {(Number(c?.status?.id) === ContractStatusEnum.Accepted) && (
                                                <Button
                                                    className="flex items-center justify-center gap-1.5 sm:gap-2 bg-red-600 hover:bg-red-700 text-white text-xs sm:text-sm h-9 sm:h-10"
                                                    onClick={() => handleCompleteContract(c)}
                                                >
                                                    <CheckCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                                    <span className="hidden sm:inline">{t("activeContractsTab.endContract")}</span>
                                                    <span className="sm:hidden">{t("activeContractsTab.end")}</span>
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                    {/* Payment Information */}
                                    <div className="border-t pt-3 sm:pt-4">
                                        <div className="mb-2 sm:mb-3">
                                            <h4 className="text-xs sm:text-sm font-medium text-gray-700 flex items-center gap-2">
                                                <DollarSign className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                                {t("activeContractsTab.paymentDetails")}
                                            </h4>
                                        </div>
                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
                                            <div className="space-y-1.5 sm:space-y-2">
                                                {isHourly ? (
                                                    <>
                                                        <div className="flex justify-between text-xs sm:text-sm">
                                                            <span className="text-gray-600">{t("activeContractsTab.hourlyRate")}</span>
                                                            <span className="font-medium">{getDynamicCurrencySymbol()}{Number(c.amount || 0).toFixed(2)}/hour</span>
                                                        </div>
                                                        <div className="flex justify-between text-xs sm:text-sm">
                                                            <span className="text-gray-600">{t("activeContractsTab.weeklyHours")}</span>
                                                            <span className="font-medium">{c.weeklyLimit || 0} hours</span>
                                                        </div>
                                                        <div className="flex justify-between text-xs sm:text-sm">
                                                            <span className="text-gray-600">{t("activeContractsTab.weeklyAmount")}</span>
                                                            <span className="font-medium">{getDynamicCurrencySymbol()}{weeklyAmount.toFixed(2)}</span>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <div className="flex justify-between text-xs sm:text-sm">
                                                        <span className="text-gray-600">{t("activeContractsTab.fixedAmount")}</span>
                                                        <span className="font-medium">{getDynamicCurrencySymbol()}{Number(c.amount || 0).toFixed(2)}</span>
                                                    </div>
                                                )}
                                                <div className="flex justify-between text-xs sm:text-sm">
                                                    <span className="text-gray-600">{t("activeContractsTab.platformFee")}</span>
                                                    <span className="font-medium">{getDynamicCurrencySymbol()}{platformFee.toFixed(2)}</span>
                                                </div>
                                                {isEmergency && emergencyCost > 0 && (
                                                    <div className="flex justify-between text-xs sm:text-sm">
                                                        <span className="text-red-600">{t("activeContractsTab.emergencyCost")}</span>
                                                        <span className="font-medium text-red-600">+{getDynamicCurrencySymbol()}{emergencyCost.toFixed(2)}</span>
                                                    </div>
                                                )}
                                                {trackerCost > 0 && (
                                                    <div className="flex justify-between text-xs sm:text-sm">
                                                        <span className="text-blue-600">{t("activeContractsTab.smartWorkTracker")}</span>
                                                        <span className="font-medium text-blue-600">+{getDynamicCurrencySymbol()}{trackerCost.toFixed(2)}</span>
                                                    </div>
                                                )}
                                                <div className="flex justify-between text-xs sm:text-sm font-semibold border-t pt-1.5 sm:pt-2 mt-1">
                                                    <span>{t("activeContractsTab.totalAmount")}</span>
                                                    <span className="text-base sm:text-lg text-green-600">{getDynamicCurrencySymbol()}{totalAmount.toFixed(2)}</span>
                                                </div>
                                            </div>
                                            <div className="space-y-1.5 sm:space-y-2 border-t lg:border-t-0 pt-3 lg:pt-0">
                                                <div className="flex justify-between items-center text-xs sm:text-sm">
                                                    <span className="text-gray-600">{t("activeContractsTab.contractType")}</span>
                                                    <ContractTypeBadge
                                                        contractType={contractType}
                                                        isHourly={isHourly}
                                                        weeklyLimit={c.weeklyLimit || 0}
                                                    />
                                                </div>
                                                <div className="flex justify-between items-center text-xs sm:text-sm">
                                                    <span className="text-gray-600">{t("activeContractsTab.status")}:</span>
                                                    <Badge variant={undefined} className={`text-xs ${getContractStatusColor(Number(c?.status?.id))}`}>
                                                        {getContractStatusText(Number(c?.status?.id))}
                                                    </Badge>
                                                </div>
                                                <div className="flex justify-between text-xs sm:text-sm">
                                                    <span className="text-gray-600">{t("activeContractsTab.created")}</span>
                                                    <span className="font-medium">
                                                        {new Date(c.createdAt).toLocaleDateString()}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        );
                    })}

                    <div className="mt-4 sm:mt-6 lg:mt-8">
                        <div className="flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4">
                            <div className="text-xs sm:text-sm text-gray-600 text-center sm:text-left">
                                {t("activeContractsTab.showing")} {((pagination.currentPage - 1) * pagination.limit) + 1} {t("activeContractsTab.to")} {Math.min(pagination.currentPage * pagination.limit, pagination.totalCount)} {t("activeContractsTab.of")} {pagination.totalCount} {t("activeContractsTab.contracts")}
                                {pagination.totalPages > 1 && (<span className="hidden sm:inline"> ({t("activeContractsTab.page")} {pagination.currentPage} {t("activeContractsTab.of")} {pagination.totalPages})</span>)}
                            </div>
                            <nav className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                                <Button variant="outline" size="sm" className="w-16 sm:w-20 text-xs sm:text-sm h-8 sm:h-9" disabled={pagination.currentPage === 1} onClick={() => handlePageChange(pagination.currentPage - 1)}>{t("activeContractsTab.previous")}</Button>
                                <div className="flex items-center gap-0.5 sm:gap-1">
                                    {Array.from({ length: Math.max(0, pagination.totalPages) }).slice(0, 5).map((_, idx) => {
                                        const page = idx + 1;
                                        return (
                                            <Button key={page} size="sm" variant={pagination.currentPage === page ? "default" : "ghost"} className="w-7 h-7 sm:w-9 sm:h-9 text-xs sm:text-sm p-0 min-w-0" onClick={() => handlePageChange(page)}>{page}</Button>
                                        );
                                    })}
                                </div>
                                <Button variant="outline" size="sm" className="w-16 sm:w-20 text-xs sm:text-sm h-8 sm:h-9" disabled={!pagination.hasNextPage} onClick={() => handlePageChange(pagination.currentPage + 1)}>{t("activeContractsTab.next")}</Button>
                            </nav>
                        </div>
                    </div>
                </div >
            ) : (
                <div className="text-center py-8 sm:py-12">
                    <div className="mb-4">
                        <svg className="h-12 w-12 text-gray-400 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7h18M3 12h18M3 17h18" /></svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">{t("activeContractsTab.noActiveContracts")}</h3>
                    <p className="text-gray-600 mb-4">{t("activeContractsTab.noActiveContractsMessage")}</p>
                    <Button className="w-full sm:w-auto">{t("activeContractsTab.browseServices")}</Button>
                </div>
            )}

            {/* Contract Milestone Modal */}
            {showContractModal && selectedContract && (
                <ContractMilestoneModal
                    isOpen={showContractModal}
                    onClose={handleCloseContractModal}
                    existingContractId={selectedContract.id}
                    defaultIsMilestone={selectedContract.isMilestone}
                />
            )}

            {/* Milestone Dialog */}
            {showMilestoneDialog && selectedContractForMilestones && selectedContractForMilestones.id && (
                <MilestoneDialog
                    isOpen={showMilestoneDialog}
                    contractId={selectedContractForMilestones.id}
                    onClose={handleCloseMilestoneDialog}
                />
            )}

            {/* Contract Completion Modal */}
            {showCompletionModal && selectedContractForCompletion && (
                <ContractCompletionModal
                    isOpen={showCompletionModal}
                    onClose={handleCloseCompletionModal}
                    onComplete={handleContractCompletion}
                    contractTitle={selectedContractForCompletion.title || "Contract"}
                    contract={selectedContractForCompletion}
                    loading={completingContract}
                />
            )}

            {/* Contract Closing Modal */}
            {showClosingModal && selectedContractForClosing && (
                <ContractClosingModal
                    isOpen={showClosingModal}
                    onClose={handleCloseClosingModal}
                    onCloseContract={handleContractClosing}
                    contractTitle={selectedContractForClosing.title || "Contract"}
                    contract={selectedContractForClosing}
                    loading={closingContract}
                />
            )}
        </>
    );
};

export default ActiveContractsTab;


